/**
 * Reusable discovery-tasks panel: the JobDetailsDialog Overview tab and the
 * JobRunDetail page, so a single run's discovery log is visible without
 * leaving the run-detail context.
 *
 * One row per discovery chain, (adapter, suburb, listing type): continuation
 * forks of one search fold into its row, while a suburb's for-sale and sold
 * searches are two rows. See aggregateDiscoveryTasks.
 */
import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import type { DiscoveryTaskInfo } from "@/lib/api";
import { useDebounce } from "@/hooks/useDebounce";
import {
  aggregateDiscoveryTasks,
  type DiscoveryChain,
  LISTING_LANE_LABELS,
} from "./aggregateDiscoveryTasks";
import { DiscoveryUrlBubble } from "./DiscoveryUrlBubble";

const DISCOVERY_VISIBLE_STEP = 20;

interface DiscoveryTasksPanelProps {
  tasks: DiscoveryTaskInfo[];
  /** Optional header label override. Defaults to "Discovery Tasks". */
  title?: string;
}

function DiscoveryRowStats({ agg }: { agg: DiscoveryChain }) {
  const raw = agg.urlsRawTotal || agg.urlsFound;
  const inSuburb = agg.urlsInSuburbTotal || agg.urlsFound;
  const enqueued = agg.urlsNewTotal || agg.urlsFound;
  const droppedOutOfSuburb = Math.max(0, raw - inSuburb);
  const duplicates = Math.max(0, inSuburb - enqueued - agg.urlsOutOfScopeTotal);

  const fmt = (n: number) => n.toLocaleString();
  const segments: string[] = [];
  segments.push(`${fmt(agg.totalPages)} page${agg.totalPages !== 1 ? "s" : ""} crawled`);
  if (agg.chainLength > 1) segments.push(`${agg.chainLength} forks`);
  if (raw > 0) {
    segments.push(`${fmt(raw)} URLs returned`);
    if (droppedOutOfSuburb > 0) segments.push(`${fmt(droppedOutOfSuburb)} dropped (out of suburb)`);
    segments.push(`${fmt(enqueued)} enqueued`);
    if (duplicates > 0) segments.push(`${fmt(duplicates)} duplicates`);
    if (agg.urlsOutOfScopeTotal > 0) {
      segments.push(`${fmt(agg.urlsOutOfScopeTotal)} out of date range`);
    }
  } else if (agg.totalPages === 0) {
    segments.push("not started");
  } else {
    segments.push("no listings returned");
  }

  return (
    <div className="text-[11px] text-muted-foreground leading-snug">
      {segments.join(" · ")}
    </div>
  );
}

export function DiscoveryTasksPanel({ tasks, title = "Discovery Tasks" }: DiscoveryTasksPanelProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 200);
  const [visibleCount, setVisibleCount] = useState(DISCOVERY_VISIBLE_STEP);

  const aggregated = useMemo(() => aggregateDiscoveryTasks(tasks), [tasks]);

  const filtered = useMemo(() => {
    const tokens = debouncedSearch.toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return aggregated;
    return aggregated.filter((agg) => {
      const lane = LISTING_LANE_LABELS[agg.listingType] ?? agg.listingType;
      const hay = `${agg.suburb_name} ${agg.adapter_slug} ${lane}`.toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [aggregated, debouncedSearch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset paging when search changes
  useEffect(() => {
    setVisibleCount(DISCOVERY_VISIBLE_STEP);
  }, [debouncedSearch]);

  const visible = filtered.slice(0, visibleCount);

  if (tasks.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-8">
        No discovery tasks recorded for this run yet.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium flex items-center gap-2">
          <Search className="w-4 h-4" />
          {title} ({filtered.length}
          {filtered.length !== aggregated.length ? ` of ${aggregated.length}` : ""})
        </p>
      </div>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search suburb, adapter or listing type…"
        className="h-8 text-xs"
      />
      <div className="space-y-1">
        {visible.map((agg) => {
          const allComplete = agg.statuses.every((s) => s === "completed" || s === "failed");
          const anyFailed = agg.statuses.includes("failed");
          const anyActive = agg.statuses.some(
            (s) => s === "in_progress" || s === "pending" || s === "processing",
          );
          const summaryStatus = anyActive
            ? "in_progress"
            : anyFailed && agg.urlsFound === 0
              ? "failed"
              : allComplete
                ? "completed"
                : "pending";
          return (
            <div
              key={agg.key}
              className="space-y-1 text-xs p-2 rounded bg-muted/30 overflow-hidden"
            >
              <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Badge variant="outline" className="text-xs px-1.5">
                    {agg.state}
                  </Badge>
                  <span className="font-medium truncate max-w-[140px]" title={agg.suburb_name}>
                    {agg.suburb_name}
                  </span>
                  <span className="text-muted-foreground">×</span>
                  <span className="text-muted-foreground truncate max-w-[140px]" title={agg.adapter_slug}>
                    {agg.adapter_slug}
                  </span>
                  <Badge variant="secondary" className="text-[10px] px-1.5">
                    {LISTING_LANE_LABELS[agg.listingType] ?? agg.listingType}
                  </Badge>
                </div>
                <DiscoveryUrlBubble url={agg.searchUrl} />
                <Badge
                  variant="outline"
                  className={
                    summaryStatus === "completed"
                      ? "text-green-600 border-green-300"
                      : summaryStatus === "failed"
                        ? "text-red-600 border-red-300"
                        : summaryStatus === "in_progress"
                          ? "text-blue-600 border-blue-300"
                          : "text-gray-500"
                  }
                >
                  {summaryStatus === "in_progress" ? "running" : summaryStatus}
                </Badge>
              </div>
              <DiscoveryRowStats agg={agg} />
              {agg.errorMessage && (
                <p className="text-[10px] text-red-600 truncate" title={agg.errorMessage}>
                  error: {agg.errorMessage}
                </p>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-4">
            No tasks match this search
          </div>
        )}
        {filtered.length > visibleCount && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full h-8 text-xs"
            onClick={() => setVisibleCount((c) => c + DISCOVERY_VISIBLE_STEP)}
          >
            Show more (+{Math.min(DISCOVERY_VISIBLE_STEP, filtered.length - visibleCount)})
          </Button>
        )}
      </div>
    </div>
  );
}
